import { isInsideUserRoster } from "chalet-shared";
import { toast } from "react-toastify";
import { combineEpics } from "redux-observable";
import { from, of, timer } from "rxjs";
import { catchError, debounce, filter, first, groupBy, map, merge, mergeMap } from "rxjs/operators";

import { Action, actionCreators, isOfAction } from "../actions";
import { ProtoError } from "../actions/flux";
import { getMaxRosterRequestSize } from "../reducers/config";
import { isLoadingByRoomId } from "../reducers/history";
import {
    getFlattenRoomViews,
    getNotSleepingActiveViewId,
    getUnreadFromHereMessages,
} from "../reducers/rooms";
import {
    checkUsersHaveName,
    getMonologUserId,
    getRoom,
    getRoomsToLoad,
    getRoster,
    getRosterWithBadges,
    getRosterWithFollowing,
    getUsersToLoad,
} from "../reducers/roster";
import { filterUndefined } from "../utils/any";
import { Epic } from "../utils/types";

function waitUntil(condition: () => boolean) {
    return timer(0, 300).pipe(
        filter(condition),
        merge(timer(5000)), // timeout
        first()
    );
}

const onSetProtoToken: Epic = (action$, state$, { App }) =>
    action$.pipe(
        filter(isOfAction(actionCreators.setProtoToken)),
        mergeMap(({ payload: { token } }) => {
            App.proto.setSessionToken(token || "");
            return [];
        })
    );

const onSetReceiptsEnqueue: Epic = (action$, state$) =>
    action$.pipe(
        filter(isOfAction(actionCreators.setReceiptsEnqueued)),
        groupBy(({ payload }) => payload.roomId),
        mergeMap(perRoomAction$ =>
            perRoomAction$.pipe(
                debounce(({ payload: { roomId } }) => {
                    const notLoading = () => !isLoadingByRoomId(state$.value, roomId);
                    return waitUntil(notLoading);
                }),
                map(({ payload }) => actionCreators.setReceiptsStarted(payload))
            )
        )
    );

const onSetReceiptsStarted: Epic = (action$, state$, { App }) =>
    action$.pipe(
        filter(isOfAction(actionCreators.setReceiptsStarted)),
        mergeMap(({ payload: { roomId, ts } }) =>
            from(App.proto.setReceipts(roomId, ts)).pipe(
                mergeMap(x => [
                    actionCreators.setReceiptsSucceeded({ roomId }),
                    actionCreators.onReceipts(x),
                ]),
                catchError(err => {
                    console.error("failed to set receipt", err);
                    return [actionCreators.setReceiptsFailed({ roomId })];
                })
            )
        )
    );

const onReceipts: Epic = (action$, state$, { App }) =>
    action$.pipe(
        filter(isOfAction(actionCreators.onReceipts)),
        mergeMap(({ payload: { receipts } }) => {
            const userIds = receipts.reduce<string[]>((acc, x) => acc.concat(x.user_ids), []);
            const unknownUserIds = checkUsersHaveName(state$.value, userIds);
            if (unknownUserIds.length) {
                return [
                    actionCreators.ensureUsersLoadedRequested({
                        userIds: unknownUserIds,
                        tag: "[receipts_user_ids]",
                    }),
                ];
            } else {
                return [];
            }
        })
    );

const onEnsureUsersLoadedRequested: Epic = (action$, state$, { App }) =>
    action$.pipe(
        filter(isOfAction(actionCreators.ensureUsersLoadedRequested)),
        mergeMap(({ payload: { userIds, tag } }) => {
            const usersToLoad = getUsersToLoad(state$.value, userIds);
            if (usersToLoad.length) {
                return [actionCreators.ensureUsersLoadedStarted({ userIds: usersToLoad, tag })];
            } else {
                return [];
            }
        })
    );

const onEnsureUsersLoadedStarted: Epic = (action$, state$, { App }) =>
    action$.pipe(
        filter(isOfAction(actionCreators.ensureUsersLoadedStarted)),
        mergeMap(({ payload: { userIds, tag } }) => {
            const maxSize = getMaxRosterRequestSize(state$.value);
            const firstUserIds = userIds.slice(0, maxSize);
            const restUserIds = userIds.slice(maxSize);
            return from(App.proto.getRoster({ user_ids: firstUserIds }, { tag })).pipe(
                mergeMap(data => [
                    actionCreators.onRoster({ data }),
                    actionCreators.ensureUsersLoadedSucceeded({ userIds }),
                    actionCreators.ensureUsersLoadedRequested({ userIds: restUserIds, tag }),
                ]),
                catchError(error => {
                    console.error("failed to get roster (users)", userIds, error);
                    return [actionCreators.ensureUsersLoadedFailed({ userIds })];
                })
            );
        })
    );

const onEnsureRoomsLoadedRequested: Epic = (action$, state$, { App }) =>
    action$.pipe(
        filter(isOfAction(actionCreators.ensureRoomsLoadedRequested)),
        mergeMap(({ payload: { roomIds, tag } }) => {
            const roomsToLoad = getRoomsToLoad(state$.value, roomIds);
            if (roomsToLoad.length) {
                return [actionCreators.ensureRoomsLoadedStarted({ roomIds: roomsToLoad, tag })];
            } else {
                return [];
            }
        })
    );

const onEnsureRoomsLoadedStarted: Epic = (action$, state$, { App }) =>
    action$.pipe(
        filter(isOfAction(actionCreators.ensureRoomsLoadedStarted)),
        mergeMap(({ payload: { roomIds, tag } }) => {
            const maxSize = getMaxRosterRequestSize(state$.value);
            const firstRoomIds = roomIds.slice(0, maxSize);
            const restRoomIds = roomIds.slice(maxSize);
            return from(App.proto.getRoster({ room_ids: firstRoomIds }, { tag })).pipe(
                mergeMap(data => [
                    actionCreators.onRoster({ data }),
                    actionCreators.ensureRoomsLoadedSucceeded({ roomIds }),
                    actionCreators.ensureRoomsLoadedRequested({ roomIds: restRoomIds, tag }),
                ]),
                catchError(error => {
                    console.error("failed to get roster (rooms)", roomIds, error);
                    return [actionCreators.ensureRoomsLoadedFailed({ roomIds })];
                })
            );
        })
    );

const onRemoveRoomFromRoster: Epic = (action$, state$, { App }) =>
    action$.pipe(
        filter(isOfAction(actionCreators.removeRoomFromRoster)),
        map(({ roomView, ...rest }) => {
            const room = getRoom(state$.value, roomView.roomId);
            return { roomView, interactionTs: room && room.interaction_ts, ...rest };
        }),
        mergeMap(({ roomView, forumId, interactionTs }) =>
            from(App.proto.setInteractionTimestamps([{ room_id: roomView.roomId, ts: 0 }])).pipe(
                mergeMap(data => {
                    const actions: Action[] = [
                        actionCreators.onRoster({ data }),
                        actionCreators.SeenAllText({ room_id: roomView.roomId }),
                        actionCreators.closeRoomRequested([roomView]),
                    ];
                    if (forumId) {
                        actions.push(
                            actionCreators.setForumLevelStarted({
                                forumId,
                                level: "watch",
                            })
                        );
                    }
                    if (interactionTs) {
                        actions.push(
                            actionCreators.showUndoResetInteractionsToaster({
                                roomViews: [roomView],
                                interactions: [{ room_id: roomView.roomId, ts: interactionTs }],
                            })
                        );
                    }
                    return actions;
                }),
                catchError(error => {
                    console.error("failed to reset interactions", roomView.roomId, error);
                    return [actionCreators.removeRoomFromRosterError(roomView)];
                })
            )
        )
    );

const onShowUndoResetInteractionsToaster: Epic = action$ =>
    action$.pipe(
        filter(isOfAction(actionCreators.showUndoResetInteractionsToaster)),
        mergeMap(({ payload }) =>
            from(
                new Promise<boolean>(resolve => {
                    toast("Undo", {
                        onClick: () => {
                            resolve(true);
                        },
                        onClose: () => {
                            resolve(false);
                        },
                        autoClose: 15000,
                    });
                })
            ).pipe(
                filter(clicked => clicked),
                map(() => actionCreators.undoResetInteractions(payload))
            )
        )
    );

const onUndoResetInteractions: Epic = (action$, state$, { App }) =>
    action$.pipe(
        filter(isOfAction(actionCreators.undoResetInteractions)),
        mergeMap(({ payload: { roomViews, interactions } }) =>
            from(App.proto.setInteractionTimestamps(interactions)).pipe(
                mergeMap(data => [
                    actionCreators.onRoster({ data }),
                    ...roomViews.map(roomView => actionCreators.openRoomRequested({ roomView })),
                ]),
                catchError(error => {
                    console.error("failed to undo reset interactions", roomViews, error);
                    return [
                        actionCreators.undoResetInteractionsError({
                            retryArgs: { roomViews, interactions },
                        }),
                    ];
                })
            )
        )
    );

// TODO: "from here" is no longer the case -- rename
const onMarkRoomUnreadFromHere: Epic = (action$, state$, { App }) =>
    action$.pipe(
        filter(isOfAction(actionCreators.markRoomUnreadFromHere)),
        mergeMap(({ payload: { roomView, ts } }) => {
            const { roomId } = roomView;
            const activeId = getNotSleepingActiveViewId(state$.value);
            const monologUserId = getMonologUserId(state$.value);
            if (!monologUserId) {
                return [];
            }
            const { tssRead, tssUnread, tssUnreadMentions, receiptTs } = getUnreadFromHereMessages(
                state$.value,
                roomId,
                monologUserId,
                ts
            );
            const actions = [] as Action[];
            if (tssRead.length + tssUnread.length === 0) {
                return [];
            }
            actions.push(
                actionCreators.setReceiptsEnqueued({ roomId, ts: receiptTs, monologUserId })
            );
            if (activeId === roomView.viewId) {
                actions.push(actionCreators.setRoomViewAsSleeping(roomView));
            }
            if (tssRead.length) {
                const seen = { room_id: roomId, tss: tssRead };
                App.proto.markSeenText(seen).then(
                    data => {
                        App.actionCreators.onRoster({ data });
                        App.actionCreators.updateRoomFirstUnread({ roomId });
                    },
                    error => {
                        console.error("Failed to mark text seen", roomId, ts, error);
                    }
                );
                actions.push(actionCreators.onSeenText(seen));
            }
            if (tssUnread.length) {
                const unseen = {
                    room_id: roomId,
                    tss: tssUnread,
                    mentioned_tss: tssUnreadMentions,
                };
                App.proto.markUnseenText(unseen).then(
                    data => {
                        App.actionCreators.onRoster({ data });
                        App.actionCreators.updateRoomFirstUnread({ roomId });
                    },
                    error => {
                        console.error("Failed to mark text unseen", roomId, ts, error);
                    }
                );
                actions.push(actionCreators.onUnseenText(unseen));
            }
            return actions;
        })
    );

const onRoster: Epic = (action$, state$, { App }) =>
    action$.pipe(
        filter(isOfAction(actionCreators.onRoster)),
        mergeMap(({ payload: { data, unsolicited } }) => {
            if (data.rooms && unsolicited) {
                const roomsNotInRoster = data.rooms.filter(
                    room => isInsideUserRoster(room) && !getRoster(state$.value).has(room.id)
                );
                if (roomsNotInRoster.length) {
                    // is safe to call multiple times
                    return [
                        actionCreators.ensureRoomsLoadedRequested({
                            roomIds: roomsNotInRoster.map(x => x.id),
                            tag: "[rooms_for_interactions]",
                        }),
                    ];
                } else {
                    return [];
                }
            } else {
                return [];
            }
        })
    );

const onGroupMarkAsRead: Epic = (action$, state$, { App }) =>
    action$.pipe(
        filter(isOfAction(actionCreators.groupMarkAsRead)),
        mergeMap(({ payload: roomGroup }) => {
            const roomIdsParam =
                roomGroup === "all"
                    ? undefined
                    : (() => {
                          const rooms = getRosterWithBadges(state$.value).filter(r => {
                              if (roomGroup === "forums") {
                                  return r.type === "forum";
                              } else if (roomGroup === "dialogs") {
                                  return r.type === "dialog";
                              } else if (roomGroup === "external") {
                                  return r.type === "contact";
                              }

                              return false;
                          });
                          const roomIds = rooms.keySeq().toArray();
                          return roomIds;
                      })();

            return from(App.proto.seenAllText(roomIdsParam, { tag: "seen_all_text" })).pipe(
                mergeMap(data =>
                    of(
                        actionCreators.onRoster({ data }),
                        actionCreators.onSeenAllText({ room_ids: roomIdsParam })
                    )
                ),
                catchError(error => {
                    console.error("groupMarkAsRead failed", error);
                    return of(actionCreators.groupMarkAsReadError(roomGroup));
                })
            );
        })
    );

const onResetInteractionTimestamp: Epic = (action$, state$, { App }) =>
    action$.pipe(
        filter(isOfAction(actionCreators.groupResetInteractionTimestamp)),
        mergeMap(({ payload: roomGroup }) => {
            const rooms = getRoster(state$.value).filter(r => {
                if (roomGroup === "forums") {
                    return r.type === "forum";
                } else if (roomGroup === "dialogs") {
                    return r.type === "dialog";
                } else if (roomGroup === "external") {
                    return r.type === "contact";
                } else if (roomGroup === "all") {
                    return true;
                }

                return false;
            });
            const undoRooms = rooms
                .valueSeq()
                .toArray()
                .map(x => ({ room_id: x.id, ts: x.interaction_ts || 0 }));
            const interactions = undoRooms.map(x => ({ ...x, ts: 0 }));

            const roomViews = getFlattenRoomViews(state$.value)
                .filter(x => interactions.some(y => x.roomId === y.room_id))
                .toArray();

            return from(App.proto.setInteractionTimestamps(interactions)).pipe(
                mergeMap(data =>
                    of(
                        actionCreators.showUndoResetInteractionsToaster({
                            roomViews,
                            interactions: undoRooms,
                        }),
                        actionCreators.onRoster({ data }),
                        actionCreators.closeRoomRequested(roomViews)
                    )
                ),
                catchError(error => {
                    console.error("failed to reset interaction timestamp(s)", error);
                    return [actionCreators.groupResetInteractionTimestampError(roomGroup)];
                })
            );
        })
    );

const onGroupUnfollow: Epic = (action$, state$, { App }) =>
    action$.pipe(
        filter(isOfAction(actionCreators.groupUnfollow)),
        mergeMap(() => {
            const rooms = getRosterWithFollowing(state$.value);
            const forumIds = filterUndefined(rooms.toArray().map(([_, x]) => x.forum_id));

            return from(App.proto.setForumLevel(forumIds, "watch")).pipe(
                mergeMap(data => of(actionCreators.onRoster({ data }))),
                catchError(error => {
                    console.error("failed to group unfollow", error);
                    return [actionCreators.groupUnfollowError()];
                })
            );
        })
    );

const onGroupHide: Epic = (action$, state$, { App }) =>
    action$.pipe(
        filter(isOfAction(actionCreators.groupHide)),
        mergeMap(({ payload: roomGroup }) =>
            roomGroup === "forums" || roomGroup === "all"
                ? of(
                      actionCreators.groupMarkAsRead(roomGroup),
                      actionCreators.groupResetInteractionTimestamp(roomGroup),
                      actionCreators.groupUnfollow()
                  )
                : of(
                      actionCreators.groupMarkAsRead(roomGroup),
                      actionCreators.groupResetInteractionTimestamp(roomGroup)
                  )
        )
    );

const onFluxAction: Epic = (action$, state$, { App }) =>
    action$.pipe(
        mergeMap(action => {
            if (action.type === actionCreators.CreateForum.type) {
                const { name, isPrivate, memberIds } = action.payload;
                App.proto.createForum(name, isPrivate, memberIds).then(
                    data => {
                        App.actionCreators.onRoster({ data });
                        App.actionCreators.CreateForumSuccess(data);
                    },
                    error => {
                        App.actionCreators.CreateForumError(new ProtoError(error, action.payload));
                    }
                );
            } else if (action.type === actionCreators.ArchiveForum.type) {
                App.proto.archiveForum(action.payload).then(
                    data => {
                        App.actionCreators.onRoster({ data });
                    },
                    error => {
                        console.error("Failed to archive forum", action, error);
                    }
                );
            } else if (action.type === actionCreators.RenameForum.type) {
                const { forum_id, name } = action.payload;
                App.proto.renameForum({ forum_id, name }).then(
                    data => {
                        App.actionCreators.onRoster({ data });
                    },
                    error => {
                        App.actionCreators.RenameForumError(new ProtoError(error, action.payload));
                    }
                );
            } else if (action.type === actionCreators.ChangeForumAccess.type) {
                const { forum_id, memberIds } = action.payload;
                App.proto.changeForumAccess(forum_id, memberIds).then(
                    data => {
                        App.actionCreators.onRoster({ data });
                    },
                    error => {
                        console.error("Failed to change forum access", action, error);
                    }
                );
            } else if (action.type === actionCreators.SeenText.type) {
                App.proto.markSeenText(action.payload).then(
                    data => {
                        App.actionCreators.onRoster({ data });
                    },
                    error => {
                        console.error("Failed to mark text seen", action, error);
                    }
                );
                App.actionCreators.onSeenText(action.payload);
            } else if (action.type === actionCreators.SeenAllText.type) {
                const { room_id } = action.payload;
                App.proto.seenAllText([room_id]).then(
                    data => {
                        App.actionCreators.onRoster({ data });
                    },
                    error => {
                        console.error("Failed to mark text seen", action, error);
                    }
                );
                App.actionCreators.onSeenAllText({ room_ids: [room_id] });
            } else if (action.type === actionCreators.MarkTextUnseen.type) {
                App.proto.markUnseenText(action.payload).then(
                    data => {
                        App.actionCreators.onRoster({ data });
                    },
                    error => {
                        console.error("Failed to mark text unseen", action, error);
                    }
                );
                App.actionCreators.onUnseenText(action.payload);
            } else if (action.type === actionCreators.setForumLevelStarted.type) {
                const { forumId, level } = action.payload;
                App.proto.setForumLevel([forumId], level).then(
                    data => {
                        App.actionCreators.onRoster({ data });
                    },
                    error => {
                        console.error("Failed to set room level", action, error);
                    }
                );
            } else if (action.type === actionCreators.GetFileURLs.type) {
                const { id, callback } = action.payload;
                callback(App.proto.getFileURLs(id));
            }
            return [];
        })
    );

export const protoEpic = combineEpics(
    onSetProtoToken,
    onSetReceiptsEnqueue,
    onSetReceiptsStarted,
    onReceipts,
    onEnsureUsersLoadedRequested,
    onEnsureUsersLoadedStarted,
    onEnsureRoomsLoadedRequested,
    onEnsureRoomsLoadedStarted,
    onUndoResetInteractions,
    onMarkRoomUnreadFromHere,
    onRemoveRoomFromRoster,
    onRoster,
    onGroupMarkAsRead,
    onShowUndoResetInteractionsToaster,
    onResetInteractionTimestamp,
    onGroupUnfollow,
    onGroupHide,
    onFluxAction
);
